import apiClient from './apiClient';
import type { ApiResponse } from '../types';
import type { TelegramNotifyCategory } from '../constants/tradingConstants';

/** Per-category mute map. A category absent from the map is ON (default). */
export type TelegramPrefs = Partial<Record<TelegramNotifyCategory, boolean>>;

/**
 * Telegram account-linking service — talks to the Cognito-authed
 * `/telegram/link` routes (TradingStack). The public `/telegram/webhook` is
 * called by Telegram, not the frontend.
 */

export interface TelegramLinkStatus {
  /** False when the feature is disabled / no bot configured for this env — the card hides. */
  available?: boolean;
  linked: boolean;
  username?: string | null;
  linked_at?: string | null;
  prefs?: TelegramPrefs;
}

export interface TelegramDeepLink {
  deep_link: string;
  bot_username: string;
  /** Seconds until the link's token expires (absent from an older backend). */
  expires_in?: number;
}

/**
 * apiClient rejects every non-2xx, so the server's own message (e.g. 503
 * "Telegram linking is temporarily unavailable. Please try again in a
 * minute.") is on the error's response body, not on a resolved value.
 */
function unwrapErr(error: any, fallback: string): Error {
  return new Error(
    error?.response?.data?.message
    || error?.response?.data?.error
    || error?.message
    || fallback,
  );
}

/** Run a /telegram/link call; a non-2xx or unsuccessful reply throws with the server's message. */
async function call<T>(request: () => Promise<ApiResponse<T>>, fallback: string): Promise<ApiResponse<T>> {
  let wrapped: ApiResponse<T>;
  try {
    wrapped = await request();
  } catch (error) {
    throw unwrapErr(error, fallback);
  }
  if (!wrapped.success) {
    throw new Error(wrapped.message || fallback);
  }
  return wrapped;
}

/** As call(), for routes whose reply must carry data. */
async function callData<T>(request: () => Promise<ApiResponse<T>>, fallback: string): Promise<T> {
  const { data } = await call(request, fallback);
  if (!data) throw new Error(fallback);
  return data;
}

class TelegramService {
  /** Current link status for the signed-in user. */
  async getStatus(): Promise<TelegramLinkStatus> {
    return callData(() => apiClient.get<ApiResponse<TelegramLinkStatus>>('/telegram/link'),
      'Failed to load Telegram status');
  }

  /** Mint a fresh deep-link the user opens in Telegram to connect. */
  async startLink(): Promise<TelegramDeepLink> {
    return callData(() => apiClient.post<ApiResponse<TelegramDeepLink>>('/telegram/link'),
      'Failed to start Telegram linking');
  }

  /** Save per-category alert preferences (true = send, false = muted). */
  async updatePrefs(prefs: TelegramPrefs): Promise<TelegramPrefs> {
    const data = await callData(
      () => apiClient.put<ApiResponse<{ prefs: TelegramPrefs }>>('/telegram/link', { prefs }),
      'Failed to save alert preferences');
    return data.prefs;
  }

  /** Disconnect Telegram for the signed-in user. */
  async unlink(): Promise<void> {
    await call(() => apiClient.delete<ApiResponse<unknown>>('/telegram/link'), 'Failed to disconnect Telegram');
  }
}

const telegramService = new TelegramService();
export default telegramService;
